import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import TopNavbar from "@/components/layout/TopNavbar";
import Footer from "@/components/home/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { motion } from "framer-motion";
import { Send, Mail, MessageCircle, Coffee, Heart, CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { emailSchema } from "@/schemas/common";
const Contact = () => {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [emailError, setEmailError] = useState<string>('');
  const [isFormValid, setIsFormValid] = useState(false);

  // Check if all fields are filled and email is valid
  useEffect(() => {
    const { name, email, message } = formData;
    const hasAllFields = name.trim() !== '' && email.trim() !== '' && message.trim() !== '';
    
    // Validate email if it's not empty
    let isEmailValid = true;
    if (email.trim() !== '') {
      try {
        emailSchema.parse(email);
        setEmailError('');
        isEmailValid = true;
      } catch (error: any) {
        const errorMessage = error.errors?.[0]?.message || 'Invalid email address';
        setEmailError(errorMessage);
        isEmailValid = false;
      }
    } else {
      setEmailError('');
      isEmailValid = false; // Email is required
    }
    
    setIsFormValid(hasAllFields && isEmailValid);
  }, [formData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Show loading toast
    const loadingToast = toast.loading('Sending your message...');
    setSubmitting(true);

    try {
      const res = await fetch('/api/contact-form', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast.dismiss(loadingToast);
        toast.success(
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />
            <div>
              <p className="font-medium">Message sent successfully!</p>
              <p className="text-sm text-gray-600">We'll get back to you within 2 hours.</p>
            </div>
          </div>,
          { duration: 5000 }
        );
        
        // Reset form
        setFormData({ name: '', email: '', message: '' });
        const form = e.target as HTMLFormElement;
        form.reset();
      } else {
        throw new Error('Failed to send message');
      }
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(
        <div>
          <p className="font-medium">Failed to send message</p>
          <p className="text-sm text-gray-600">Please try again or email us directly at info@hivetivity.com</p>
        </div>,
        { duration: 5000 }
      );
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      <Helmet>
        <title>Contact Us - Hivetivity | Let's Start a Conversation</title>
        <meta name="description" content="Ready to transform your youth program? Get in touch with our friendly team and discover how Hivetivity can help you thrive." />
      </Helmet>
      
      {/* Subtle animated background matching homepage */}
      <div className="absolute inset-0 z-0 opacity-20 pointer-events-none">
        <motion.div 
          className="absolute top-10 left-10 w-64 h-64 rounded-full bg-[#FA8C00] blur-3xl"
          animate={{ 
            x: [0, 30, 0],
            y: [0, -20, 0],
          }}
          transition={{ 
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="absolute bottom-10 right-10 w-80 h-80 rounded-full bg-[#4ba3c7] blur-3xl"
          animate={{ 
            x: [0, -20, 0],
            y: [0, 15, 0],
          }}
          transition={{ 
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="absolute top-1/2 left-1/2 w-32 h-32 rounded-full bg-[#7ad4a0] blur-2xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ 
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>
      
      <TopNavbar />
      
      <main className="pt-16 relative z-10">
        {/* Hero Section */}
        <section className="py-8 md:py-12 lg:py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex justify-center mb-4 md:mb-6">
                <motion.div
                  animate={{ 
                    rotate: [0, 5, -5, 0],
                  }}
                  transition={{ 
                    duration: 6,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <Coffee className="h-8 w-8 md:h-12 md:w-12 text-[#FA8C00]" />
                </motion.div>
              </div>
              
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 md:mb-6 leading-tight px-2">
                Let's start a <span className="text-[#FA8C00] italic">conversation</span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-6 md:mb-8 max-w-2xl mx-auto leading-relaxed px-2">
                Ready to transform your youth program? Our friendly team is here to help you discover how Hivetivity can make your life easier and your program thrive.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Main Contact Section */}
        <section className="pb-8 md:pb-16 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
              
              {/* Left Side - Contact Info & Personality */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="space-y-6 lg:space-y-8"
              >
                {/* Contact Cards */}
                <div className="space-y-4 md:space-y-6">
                  <motion.div 
                    className="bg-white p-4 md:p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="flex items-center gap-3 md:gap-4 mb-3 md:mb-4">
                      <div className="bg-[#FA8C00]/10 p-2 md:p-3 rounded-full flex-shrink-0">
                        <Mail className="h-5 w-5 md:h-6 md:w-6 text-[#FA8C00]" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-lg md:text-xl font-semibold text-gray-800">Drop us a line</h3>
                        <p className="text-sm md:text-base text-gray-600">We typically respond within 2 hours</p>
                      </div>
                    </div>
                    <p className="text-base md:text-lg font-medium text-[#FA8C00] break-all">info@hivetivity.com</p>
                  </motion.div>

                  <motion.div 
                    className="bg-white p-4 md:p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="flex items-center gap-3 md:gap-4 mb-3 md:mb-4">
                      <div className="bg-[#4ba3c7]/10 p-2 md:p-3 rounded-full flex-shrink-0">
                        <MessageCircle className="h-5 w-5 md:h-6 md:w-6 text-[#4ba3c7]" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-lg md:text-xl font-semibold text-gray-800">What to expect</h3>
                        <p className="text-sm md:text-base text-gray-600">A genuine conversation about your needs</p>
                      </div>
                    </div>
                    <ul className="space-y-2 text-sm md:text-base text-gray-600">
                      <li className="flex items-center gap-2">
                        <Heart className="h-4 w-4 text-[#F75C57] flex-shrink-0" />
                        <span>No pushy sales tactics</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Heart className="h-4 w-4 text-[#F75C57] flex-shrink-0" />
                        <span>Honest answers to your questions</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Heart className="h-4 w-4 text-[#F75C57] flex-shrink-0" />
                        <span>Helpful insights for your program</span>
                      </li>
                    </ul>
                  </motion.div>
                </div>

                {/* Fun Quote */}
                <motion.div 
                  className="bg-gradient-to-r from-[#7ad4a0]/10 to-[#4ba3c7]/10 p-4 md:p-6 rounded-2xl border border-[#7ad4a0]/20"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                >
                  <blockquote className="text-base md:text-lg italic text-gray-700 mb-3 md:mb-4 leading-relaxed">
                    "We believe every youth program has the potential to be extraordinary. Sometimes it just needs the right tools to get there."
                  </blockquote>
                  <p className="text-sm text-gray-500">— The Hivetivity Team</p>
                </motion.div>
              </motion.div>

              {/* Right Side - Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <div className="bg-white p-4 md:p-6 lg:p-8 rounded-2xl shadow-xl border border-gray-100">
                  <div className="mb-4 md:mb-6">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">Send us a message</h2>
                    <p className="text-sm md:text-base text-gray-600">Tell us about your program and how we can help</p>
                    <p className="text-xs text-gray-500 mt-2"><span className="text-[#FA8C00]">*</span> Required fields</p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                          Your name <span className="text-[#FA8C00]">*</span>
                        </label>
                        <Input 
                          id="name" 
                          type="text" 
                          required 
                          value={formData.name}
                          onChange={handleInputChange}
                          className="w-full border-gray-200 focus:border-[#FA8C00] focus:ring-[#FA8C00]/20 rounded-xl transition-all duration-200"
                          placeholder="John Smith"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                          Email address <span className="text-[#FA8C00]">*</span>
                        </label>
                        <Input 
                          id="email" 
                          type="email" 
                          required 
                          value={formData.email}
                          onChange={handleInputChange}
                          className={`w-full rounded-xl transition-all duration-200 ${
                            emailError && formData.email.trim() !== '' 
                              ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' 
                              : 'border-gray-200 focus:border-[#FA8C00] focus:ring-[#FA8C00]/20'
                          }`}
                          placeholder="john@example.com"
                        />
                        {emailError && formData.email.trim() !== '' && (
                          <div className="flex items-center gap-1 mt-1 text-sm text-red-600">
                            <AlertCircle className="h-3 w-3" />
                            <span>{emailError}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                        Tell us about your program <span className="text-[#FA8C00]">*</span>
                      </label>
                      <Textarea 
                        id="message" 
                        required 
                        value={formData.message}
                        onChange={handleInputChange}
                        className="min-h-[120px] md:min-h-[140px] border-gray-200 focus:border-[#FA8C00] focus:ring-[#FA8C00]/20 rounded-xl transition-all duration-200" 
                        placeholder="I run a dance studio with 150 students and I'm looking for better ways to manage scheduling and communicate with parents..."
                      />
                    </div>
                    
                    <motion.div
                      whileHover={isFormValid ? { scale: 1.02 } : {}}
                      whileTap={isFormValid ? { scale: 0.98 } : {}}
                    >
                      <Button 
                        type="submit" 
                        className={`
                          w-full py-4 md:py-6 rounded-xl shadow-lg transition-all duration-300 group
                          ${isFormValid 
                            ? 'bg-gradient-to-r from-[#FA8C00] to-[#FA8C00]/90 hover:from-[#FA8C00]/90 hover:to-[#FA8C00]/80 text-white hover:shadow-xl' 
                            : 'bg-gradient-to-r from-[#FED7AA] to-[#FED7AA]/90 text-[#9A3412] cursor-not-allowed'
                          }
                        `}
                        disabled={submitting || !isFormValid}
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-4 h-4 md:w-5 md:h-5 mr-2 animate-spin" />
                            <span className="text-sm md:text-base">Sending your message...</span>
                          </>
                        ) : (
                          <>
                            <Send className={`w-4 h-4 md:w-5 md:h-5 mr-2 transition-transform duration-300 ${isFormValid ? 'group-hover:translate-x-1' : ''}`} />
                            <span className="text-sm md:text-base">Send message</span>
                          </>
                        )}
                      </Button>
                    </motion.div>
                  </form>

                  <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t border-gray-100">
                    <p className="text-xs text-gray-500 text-center">
                      We respect your privacy. Your information will never be shared with third parties.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
      
      <div className="relative z-20">
        <Footer />
      </div>
    </div>
  );
};
export default Contact;